import { useInfiniteQuery } from '@tanstack/react-query';
import { getLocationRepayments, queryKeys } from '@vgrubs/components';

const PAGE_SIZE = 20;

/**
 * Every repayment of a location, newest first, a page at a time, for the
 * history modal. Only runs while the modal is open, and refetches each time
 * it opens: collections land in the background (twice a day from the blue
 * card, on every order and delivery), so a cached list goes stale quickly.
 */
export const useLocationRepayments = (locationId: number, enabled: boolean) => {
  const query = useInfiniteQuery({
    queryKey: queryKeys.admin.receivables.repayments(locationId),
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const { data } = await getLocationRepayments(locationId, {
        page: pageParam,
        limit: PAGE_SIZE,
      });
      return data.data;
    },
    getNextPageParam: (last) =>
      last.page * last.limit < last.total ? last.page + 1 : undefined,
    enabled: enabled && Number.isFinite(locationId),
    staleTime: 0,
  });

  return {
    repayments: query.data?.pages.flatMap((page) => page.repayments) ?? [],
    total: query.data?.pages[0]?.total ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    hasNextPage: !!query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
  };
};
